import { HabitNote, CreateHabitNoteRequest, UpdateHabitNoteRequest } from '../types';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required but not configured');
}

class HabitNotesApiService {
  private async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token from localStorage for authenticated requests
    const token = localStorage.getItem('auth-store');
    let authToken = null;
    
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token;
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }

    if (authToken) {
      defaultHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(endpoint, config);
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.Message || errorMessage;
        } catch {
          // If response is not JSON, use the default error message
        }
        
        throw new Error(errorMessage);
      }

      // Handle empty responses (like delete)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return {} as T;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  private getNotesUrl(habitId: string): string {
    return `${API_BASE_URL}/habits/${habitId}/notes`;
  }

  private getNoteUrl(habitId: string, noteId: string): string {
    return `${API_BASE_URL}/habits/${habitId}/notes/${noteId}`;
  }

  async createNote(habitId: string, request: CreateHabitNoteRequest): Promise<HabitNote> {
    const response = await this.makeAuthenticatedRequest<HabitNote>(
      this.getNotesUrl(habitId),
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    return response;
  }

  async getHabitNotes(habitId: string): Promise<HabitNote[]> {
    const response = await this.makeAuthenticatedRequest<HabitNote[]>(
      this.getNotesUrl(habitId)
    );
    return response;
  }

  async getNoteById(habitId: string, noteId: string): Promise<HabitNote> {
    const response = await this.makeAuthenticatedRequest<HabitNote>(
      this.getNoteUrl(habitId, noteId)
    );
    return response;
  }

  async updateNote(
    habitId: string, 
    noteId: string, 
    request: UpdateHabitNoteRequest
  ): Promise<HabitNote> {
    const response = await this.makeAuthenticatedRequest<HabitNote>(
      this.getNoteUrl(habitId, noteId),
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    );
    return response;
  }

  async deleteNote(habitId: string, noteId: string): Promise<void> {
    await this.makeAuthenticatedRequest<void>(
      this.getNoteUrl(habitId, noteId),
      {
        method: 'DELETE',
      }
    );
  }
}

// Export singleton instance
export const habitNotesApiService = new HabitNotesApiService(); 